/**
 * 手机号 localStorage 处理工具函数
 */

import { getCountryCodeByDomain } from "./countryConfigUtils";

const MOBILE_STORAGE_KEY = "user_mobile";

const getMobileStorageKey = (userId?: string | number): string => {
  if (userId === undefined || userId === null || userId === "") {
    return MOBILE_STORAGE_KEY;
  }

  return `${MOBILE_STORAGE_KEY}_${userId}`;
};

/**
 * 处理手机号前的0（根据国家规则）
 * @param mobile 手机号
 * @returns 处理后的手机号
 */
export const processMobilePrefix = (mobile: string): string => {
  if (!mobile || !mobile.trim()) {
    return mobile;
  }

  const trimmedMobile = mobile.trim();

  // 获取当前国家代码
  const countryCode = getCountryCodeByDomain();

  let result: string;

  // 贝宁（229）不需要处理0开头
  if (countryCode === 229) {
    result = trimmedMobile;
  } else {
    // 其他国家移除0开头
    const startsWithZero = trimmedMobile.startsWith("0");
    result = startsWithZero ? trimmedMobile.substring(1) : trimmedMobile;
  }

  return result;
};

/**
 * 保存手机号到 localStorage
 * @param mobile 手机号
 */
export const saveMobileToStorage = (
  mobile: string,
  userId?: string | number
) => {
  if (mobile && mobile.trim()) {
    // 使用统一的手机号处理方法
    const processedMobile = processMobilePrefix(mobile);
    localStorage.setItem(getMobileStorageKey(userId), processedMobile);
  }
};

/**
 * 从 localStorage 获取手机号
 * @returns 保存的手机号或空字符串
 */
export const getMobileFromStorage = (): string => {
  return localStorage.getItem(MOBILE_STORAGE_KEY) || "";
};

const getMobileFromUserStorage = (userId?: string | number): string => {
  if (userId === undefined || userId === null || userId === "") {
    return "";
  }

  return localStorage.getItem(getMobileStorageKey(userId)) || "";
};

/**
 * 清除 localStorage 中的手机号
 */
export const clearMobileFromStorage = () => {
  localStorage.removeItem(MOBILE_STORAGE_KEY);
};

/**
 * 获取用户手机号，优先从当前账号 localStorage 获取，其次从用户信息获取
 * @param userMobile 用户信息中的手机号
 * @returns 处理后的手机号
 */
export const getUserMobile = (
  userMobile?: string,
  userId?: string | number
): string => {
  // 优先从当前账号 localStorage 获取；没有 userId 时保留历史全局缓存兼容。
  const storedMobile =
    getMobileFromUserStorage(userId) ||
    (userId === undefined || userId === null || userId === ""
      ? getMobileFromStorage()
      : "");
  if (storedMobile) {
    return storedMobile;
  }

  // 其次从用户信息获取
  if (userMobile) {
    const processedMobile = processMobilePrefix(userMobile);
    return processedMobile;
  }

  return "";
};

/**
 * 获取提现手机号，只使用当前登录用户信息，避免换号后复用上一个账号的缓存手机号
 * @param userMobile 用户信息中的手机号
 * @param userId 用户Id
 * @returns 处理后的手机号
 */
export const getWithdrawalMobile = (
  userMobile?: string,
  userId?: string | number
): string => {
  const storedMobile = getMobileFromUserStorage(userId);
  if (storedMobile) {
    return storedMobile;
  }

  if (userMobile) {
    return processMobilePrefix(userMobile);
  }

  return "";
};
